import React from 'react';
import { Helmet } from 'react-helmet-async';

// Organization structured data for the website
export function OrganizationStructuredData() {
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "PlanCrafted",
    "url": "https://plancrafted.com",
    "logo": "https://static.plancrafted.com/img/logo.png",
    "description": "Custom woodworking plans with 3D previews. Generate detailed project plans tailored to your specifications.",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "url": "https://plancrafted.com/contact"
    },
    "sameAs": [
      "https://www.facebook.com/plancrafted",
      "https://twitter.com/plancrafted"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>
    </Helmet>
  );
}

// Product structured data for individual models
export function ProductStructuredData({ model }) {
  if (!model) return null;

  const productData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": model.name || `${model.name} Woodworking Plan`,
    "description": model.description || `Custom ${model.name} woodworking plans with detailed measurements and 3D visualization.`,
    "image": model.images?.sample?.[0]?.image || model.images?.sample?.[0] || "https://static.plancrafted.com/img/default-model.jpg",
    "brand": {
      "@type": "Brand",
      "name": "PlanCrafted"
    },
    "category": "Woodworking Plans",
    "offers": {
      "@type": "Offer",
      "price": model.amount || "9.99",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "url": `https://plancrafted.com/designs/${model.name}`,
      "seller": {
        "@type": "Organization",
        "name": "PlanCrafted"
      }
    }
  };

  // Add additional properties if available
  if (model.meta?.dimensions) {
    productData.width = model.meta.dimensions.width;
    productData.height = model.meta.dimensions.height;
    productData.depth = model.meta.dimensions.depth;
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(productData)}
      </script>
    </Helmet>
  );
}

// Website structured data for homepage
export function WebsiteStructuredData() {
  const websiteData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "PlanCrafted",
    "url": "https://plancrafted.com",
    "description": "Generate customizable woodworking plans with 3D previews. Create detailed project plans tailored to your specifications.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://plancrafted.com/models?search={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(websiteData)}
      </script>
    </Helmet>
  );
}

// Breadcrumb structured data
export function BreadcrumbStructuredData({ items }) {
  if (!items || items.length < 2) return null;

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://plancrafted.com${item.url}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>
    </Helmet>
  );
}

// FAQ structured data
export function FAQStructuredData({ faqs }) {
  if (!faqs || faqs.length === 0) return null;

  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(faqData)}
      </script>
    </Helmet>
  );
}